import { useMemo } from 'react';
import { useDaggerheartData } from '../context/DataContext';
import {
  getFrameConfig,
  getFrameMechanics,
  getFrameMechanicConfig,
  getDowntimeOverride,
  FLAVOR_CONFIG,
  getFlavorColorClass,
  getFlavorDieSides,
  getMatchingAlgorithm,
  BENEFIT_TYPES,
} from '../config/campaignFrameConfig';

/**
 * useFrameConfig - Hook to access campaign frame configuration and static data
 *
 * Combines:
 * - Static data from campaign_frames.json (weapons, armor, ingredient tables, lore)
 * - Mechanic patterns from campaignFrameConfig.js (behavior definitions)
 *
 * @param {string} frameId - The frame identifier (e.g., 'beast-feast', 'witherwild')
 * @returns {object} Combined frame data and utilities
 *
 * @example
 * const { frame, mechanics, hasCustomMechanics, getFlavorColor } = useFrameConfig('beast-feast');
 * if (hasCustomMechanics) {
 *   const cookingConfig = mechanics.cooking;
 *   // Use cookingConfig.matchingAlgorithm, etc.
 * }
 */
const useFrameConfig = (frameId) => {
  const allData = useDaggerheartData();

  return useMemo(() => {
    // Core frame case - no special mechanics
    if (!frameId || frameId === 'core') {
      return {
        frameId: 'core',
        frame: null,
        staticData: null,
        mechanics: {},
        hasCustomMechanics: false,
        downtimeOverride: null,

        // Utility functions still available (return defaults)
        getFlavorColor: () => 'bg-gray-100 text-gray-800',
        getFlavorDie: () => 6,
        getMatchingAlgorithm: () => null,
        flavorConfig: FLAVOR_CONFIG,
        benefitTypes: BENEFIT_TYPES,
      };
    }

    // Get static data from campaign_frames.json (loaded via DataContext)
    const staticData = allData?.campaignFrames?.[frameId] || null;

    // Get behavior config from campaignFrameConfig.js
    const frameConfig = getFrameConfig(frameId);
    const mechanics = getFrameMechanics(frameId);
    const downtimeOverride = getDowntimeOverride(frameId);

    return {
      frameId,

      // Full frame config from campaignFrameConfig.js
      frame: frameConfig,

      // Static data from campaign_frames.json
      staticData,

      // Mechanics from config (patterns, triggers, algorithms)
      mechanics,

      // Quick check for custom mechanics
      hasCustomMechanics: Object.keys(mechanics).length > 0,

      // Downtime override (e.g., 'make-a-feast' for Beast Feast)
      downtimeOverride,

      // Utility functions
      getFlavorColor: getFlavorColorClass,
      getFlavorDie: getFlavorDieSides,
      getMatchingAlgorithm,
      flavorConfig: FLAVOR_CONFIG,
      benefitTypes: BENEFIT_TYPES,

      // Helper to get a specific mechanic
      getMechanic: (mechanicId) => getFrameMechanicConfig(frameId, mechanicId),

      // Helper to resolve a source path from static data
      // e.g., resolveSource('customMechanics.harvesting.yields') returns the yields array
      resolveSource: (sourcePath) => {
        if (!staticData || !sourcePath) return null;
        return sourcePath.split('.').reduce((obj, key) => obj?.[key], staticData);
      },
    };
  }, [frameId, allData]);
};

/**
 * useFrameMechanic - Hook to access a specific mechanic's configuration
 *
 * @param {string} frameId - The frame identifier
 * @param {string} mechanicId - The mechanic identifier (e.g., 'cooking', 'wither-corruption')
 * @returns {object|null} The mechanic config or null if not found
 *
 * @example
 * const cookingConfig = useFrameMechanic('beast-feast', 'cooking');
 * if (cookingConfig) {
 *   const algorithm = getMatchingAlgorithm(cookingConfig.matchingAlgorithm);
 * }
 */
export const useFrameMechanic = (frameId, mechanicId) => {
  return useMemo(() => {
    if (!frameId || !mechanicId) return null;
    return getFrameMechanicConfig(frameId, mechanicId);
  }, [frameId, mechanicId]);
};

export default useFrameConfig;
